/* eslint-disable max-len */
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react'
import {
  RichTextEditorComponent,
  ToolbarSettingsModel,
} from '@syncfusion/ej2-react-richtexteditor'
import { SignatureComponent } from '@syncfusion/ej2-react-inputs'
import { NoteContextType } from '@components/common/TagBadge/TagBadge.component'
import {
  AI_ICON_COLOR,
  generateAIToolbarStyles,
  generateAIToolbarButtons,
} from '../AIToolbar.utils'
import { resolveNoteBodyBlobSrc } from '@services/api/note.api'
import {
  htmlTemplateToMarkdownOutline,
  NOTES_TEMPLATE_FILL_PROMPT_TEMPLATE,
  NOTES_TEMPLATE_FILL_SYSTEM_PROMPT,
  replaceInsertedAiResponseBlock,
  useAIResponse,
} from '../useAIResponse'
import { useNotesLiveTranscribe } from './useNotesLiveTranscribe'
import { useNoteEditorRecording } from './useNoteEditorRecording'
import { useNoteEditorTemplates } from './useNoteEditorTemplates'
import {
  buildNotesAiUserSourceInput,
  buildTranscriptSummaryInput,
} from './notesTranscript.util'
import { INote, INoteTranscriptSegment } from '@dataTypes/note.type'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { fetchAllNotes } from '@store/actions/note.action'
import { getFullName } from '@utils/fullname.util'
import { buildNoteVariableList } from '@constants/Notes/noteVariables'
import {
  getContextItemDisplayName,
  type NoteFilterContextLookup,
} from '@utils/noteFilter.util'
import {
  saveNote as saveNoteAction,
  deleteNote as deleteNoteAction,
} from './useNoteEditor'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { getAppointmentsByPatientId } from '@services/api/appointment.api'
import {
  cleanupSyncfusionOrphanedTooltips,
  useSyncfusionTooltipCleanup,
} from '@components/common/NoteTextEditor/hooks/useSyncfusionTooltipCleanup'
import { isLatestNoteInVersionChain } from '@utils/noteVersion.util'

import NoteEditorHeader from './NoteEditorHeader.component'
import NoteEditorRichTextPane from './NoteEditorRichTextPane.component'
import NoteEditorQuickNote from './NoteEditorQuickNote.component'
import NoteEditorNoteInfo from './NoteEditorNoteInfo.component'
import NoteEditorContextSidebar from './NoteEditorContextSidebar.component'
import NoteEditorModals from './NoteEditorModals.component'
import {
  DEFAULT_BACK_LABEL,
  NOTES_AI_SELECT_CLINIC_TITLE,
  NOTES_TRANSCRIPT_SUMMARY_PROMPT_TEMPLATE,
  NOTES_TRANSCRIPT_SUMMARY_SYSTEM_PROMPT,
  QUICK_NOTE_AI_MODE_CONFIG,
} from './noteEditor.constants'
import {
  applySidebarFiltersToNotes,
  buildDoctorDisplayName,
  formatAppointmentContextNameFromLookup,
  formatAppointmentHeaderContextName,
  getNoteContextItemId,
  isGeneralPatientContextType,
  isNoteContentEmpty,
} from './noteEditor.helpers'
import type {
  AiPreviewSource,
  LastNotesAiGeneration,
  NewNoteSeedContext,
  NoteEditorProps,
  QuickNoteAiMode,
  RichTextEditorWithSelection,
  UnsavedModalType,
} from './noteEditor.types'
import { logger } from '@utils/safeLogger'
const NoteEditor: React.FC<NoteEditorProps> = ({
  patientName,
  contextName,
  backButtonLabel,
  onBack,
  onSave,
  note,
  onDelete,
  onToggleSidebar,
  onViewTranscription,
  onHideTranscription,
  showTranscription = false,
  onTranscriptSegmentsChange,
  mode = 'edit',
  onEditNote,
  onViewNote,
  contextId = null,
  contextType,
  patientId,
  contactId: contactIdProp,
  openContextNotesByDefault = false,
  selectFirstNote = false,
  autoFilterOnViewSelect = true,
  onNavigationGuardChange,
  onRegisterSaveAndLeave,
  onInitialContextNoteResolved,
  initialNewNoteContextType,
  initialNewNoteContextId,
  claimContextNotesReadOnly = false,
  initialTitle,
}) => {
  useSyncfusionTooltipCleanup()

  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const showToastRef = useRef(showToast)
  showToastRef.current = showToast
  const hasContextId = Boolean(contextId && contextId.trim())
  const isContactContext =
    contextType === NoteContextType.CONTACT && (contactIdProp?.trim() ?? false)
  const isCallContext = contextType === NoteContextType.CALL && hasContextId
  const hasContextNotesOwnership = hasContextId || isContactContext

  const backLabel =
    backButtonLabel ??
    (contextType && DEFAULT_BACK_LABEL[contextType]
      ? DEFAULT_BACK_LABEL[contextType]
      : 'Back')

  const [activeNote, setActiveNote] = useState<INote | null>(note ?? null)
  const [activeMode, setActiveMode] = useState<'view' | 'edit'>(() =>
    selectFirstNote || (note && mode === 'view') ? 'view' : 'edit',
  )

  const displayedNote = hasContextNotesOwnership ? activeNote : (note ?? null)
  const displayedMode = hasContextNotesOwnership ? activeMode : (mode ?? 'edit')
  const appointmentHeaderContextName = useMemo(
    () => formatAppointmentHeaderContextName(displayedNote, patientName),
    [displayedNote, patientName],
  )

  /** Parent-controlled note (stable editor key): keep internal list selection in sync when `note` prop changes. */
  const parentDrivenNoteId = note?.note_id
  // Sync the displayed note whenever the parent passes updates, but only reset view/edit mode when the
  // **note id** or parent **mode** changes. Otherwise every new `note` object reference (e.g. Redux
  // refresh while parent still passes mode="view") would snap back to view and block "Edit note".
  useEffect(() => {
    if (!hasContextNotesOwnership || !note || !parentDrivenNoteId) return
    setActiveNote(note)
  }, [hasContextNotesOwnership, note, parentDrivenNoteId])

  useEffect(() => {
    if (!hasContextNotesOwnership || !parentDrivenNoteId) return
    setActiveMode(mode === 'view' ? 'view' : 'edit')
  }, [hasContextNotesOwnership, mode, parentDrivenNoteId])

  useEffect(() => {
    if (!hasContextNotesOwnership || !selectFirstNote) return
    setActiveMode('view')
  }, [hasContextNotesOwnership, selectFirstNote])

  /** When true (e.g. Charting, Contact page), context_type is fixed to contextType and cannot be changed. */
  const tagLockedToContext = hasContextId || isContactContext

  const appointmentData = useAppSelector(
    (state) => state.appointment?.data ?? [],
  )
  const dentalCharts = useAppSelector(
    (state) => state.charting.dentalCharts ?? [],
  )
  const perioCharts = useAppSelector(
    (state) => state.charting.perioCharts ?? [],
  )
  const treatmentPlans = useAppSelector(
    (state) => state.charting.treatmentPlans ?? [],
  )
  const [appointmentsForFilter, setAppointmentsForFilter] = useState<
    Array<{
      appointment_id: string
      appt_date?: string
      appt_start?: string
      appointment_start_date?: string
    }>
  >([])
  const contextLookup: NoteFilterContextLookup = useMemo(() => {
    type AppointmentDisplay = {
      appointment_id: string
      appt_date?: string
      appt_start?: string
      appointment_start_date?: string
      appointment_start_date_time?: string
    }
    const fromState: AppointmentDisplay[] = appointmentData.map((a) => ({
      appointment_id: a.appointment_id,
      appt_date: a.appt_date,
      appt_start: a.appt_start,
      appointment_start_date: a.appointment_start_date,
      appointment_start_date_time: a.appointment_start_date,
    }))
    const byId = new Map<string, AppointmentDisplay>(
      fromState.map((a) => [a.appointment_id, a]),
    )
    appointmentsForFilter.forEach((a) => {
      byId.set(a.appointment_id, {
        appointment_id: a.appointment_id,
        appt_date: a.appt_date,
        appt_start: a.appt_start,
        appointment_start_date: a.appointment_start_date,
        appointment_start_date_time: a.appointment_start_date,
      })
    })
    return {
      appointments: Array.from(byId.values()),
      dentalCharts: dentalCharts.map((c) => ({
        chart_id: c.chart_id,
        title: c.title,
        date_time_created_at: c.date_time_created_at,
      })),
      perioCharts: perioCharts.map((c) => ({
        chart_id: c.chart_id,
        title: c.title,
        date_time_created_at: c.date_time_created_at,
      })),
      treatmentPlans: treatmentPlans.map((t) => ({
        tp_id: t.tp_id,
        tp_heading: t.tp_heading,
        date_time_created_at: t.date_time_created_at,
      })),
    }
  }, [
    appointmentData,
    appointmentsForFilter,
    dentalCharts,
    perioCharts,
    treatmentPlans,
  ])
  const routeContextName = useMemo((): string | null => {
    const normalizedContextId = contextId?.trim()
    if (!normalizedContextId || !contextType) return null

    if (contextType === NoteContextType.APPOINTMENT) {
      return formatAppointmentContextNameFromLookup(
        normalizedContextId,
        contextLookup,
        patientName,
      )
    }

    const display = getContextItemDisplayName(
      contextType,
      normalizedContextId,
      contextLookup,
    )
    if (display === 'No Context' || display === normalizedContextId) return null
    return display
  }, [contextId, contextLookup, contextType, patientName])
  const [noteTitle, setNoteTitle] = useState(
    note?.title || initialTitle || 'New Note',
  )
  const [noteContent, setNoteContent] = useState(note?.body ?? '')
  const {
    customTemplates,
    selectedTemplate,
    setSelectedTemplate,
    showTemplateDropdown,
    setShowTemplateDropdown,
    templateSearchQuery,
    setTemplateSearchQuery,
    templateDropdownPosition,
    setTemplateDropdownPosition,
    expandedCategories,
    setExpandedCategories,
    showCustomTemplateModal,
    setShowCustomTemplateModal,
    editingTemplateId,
    setEditingTemplateId,
    setTemplateButtonLabel,
    templateDropdownRef,
    filteredTemplates,
    getTemplateContent,
    getTemplateDisplayName,
    handleTemplateClick,
    handleTemplateSearch,
    handleCustomTemplateSubmit,
    handleCustomTemplateDelete,
  } = useNoteEditorTemplates({
    patientName,
    setNoteContent,
    showToast,
  })
  const [selectedTag, setSelectedTag] = useState(
    (note?.context_type as NoteContextType) ||
      (contextType === NoteContextType.CONTACT
        ? NoteContextType.CONTACT
        : NoteContextType.PATIENT),
  )
  const isGeneralPatientHeaderContext =
    isGeneralPatientContextType(contextType) ||
    isGeneralPatientContextType(selectedTag) ||
    isGeneralPatientContextType(displayedNote?.context_type)
  const headerContextName = useMemo((): string | null => {
    const trimmedPatientName = patientName?.trim() || null
    const contextIdTrimmed = contextId?.trim()
    const skipIfPatientPublicId = (
      name: string | null | undefined,
    ): string | null => {
      const trimmed = name?.trim()
      if (!trimmed) return null
      if (
        isGeneralPatientHeaderContext &&
        contextIdTrimmed &&
        trimmed === contextIdTrimmed
      ) {
        return null
      }
      return trimmed
    }

    if (isGeneralPatientHeaderContext) {
      return (
        skipIfPatientPublicId(contextName) ||
        trimmedPatientName ||
        skipIfPatientPublicId(displayedNote?.context_display_name) ||
        null
      )
    }

    return (
      contextName?.trim() ||
      appointmentHeaderContextName ||
      routeContextName ||
      skipIfPatientPublicId(displayedNote?.context_display_name) ||
      (!hasContextNotesOwnership ? trimmedPatientName : null)
    )
  }, [
    appointmentHeaderContextName,
    contextId,
    contextName,
    displayedNote?.context_display_name,
    hasContextNotesOwnership,
    isGeneralPatientHeaderContext,
    patientName,
    routeContextName,
  ])
  const isHeaderContextPending =
    hasContextNotesOwnership &&
    !isGeneralPatientHeaderContext &&
    !headerContextName?.trim()
  const [newNoteSeedContext, setNewNoteSeedContext] =
    useState<NewNoteSeedContext | null>(() =>
      initialNewNoteContextType
        ? {
            contextType: initialNewNoteContextType,
            contextId: initialNewNoteContextId?.trim() || undefined,
          }
        : null,
    )
  const [aiNoteStructure, setAiNoteStructure] = useState('')
  const [aiTone, setAiTone] = useState('')
  const [showAiNoteStructureDropdown, setShowAiNoteStructureDropdown] =
    useState(false)
  const [showAiToneDropdown, setShowAiToneDropdown] = useState(false)
  const [quickNote, setQuickNote] = useState('')
  const [quickNoteAiMode, setQuickNoteAiMode] = useState<QuickNoteAiMode>('ask')
  const [, setAiPreviewSource] = useState<AiPreviewSource>(null)
  const [showAiOverlay, setShowAiOverlay] = useState(true)
  const [isTyping, setIsTyping] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isSavingNote, setIsSavingNote] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSignaturePad, setShowSignaturePad] = useState(false)
  const [showEmptyNoteWarning, setShowEmptyNoteWarning] = useState(false)
  const [emptyNoteWarningAction, setEmptyNoteWarningAction] = useState<
    'none' | 'leave' | 'newNote'
  >('none')
  const [pendingSidebarNote, setPendingSidebarNote] = useState<INote | null>(
    null,
  )
  const [unsavedModalType, setUnsavedModalType] =
    useState<UnsavedModalType>(null)
  const [resolvedContentForView, setResolvedContentForView] = useState<
    string | null
  >(null)
  const [showTagDropdown, setShowTagDropdown] = useState(false)
  const [contextNotes, setContextNotes] = useState<INote[]>([])
  const [contextNotesFetched, setContextNotesFetched] = useState(false)
  const [selectFirstNoteResolved, setSelectFirstNoteResolved] = useState(false)
  const [sidebarSearchQuery, setSidebarSearchQuery] = useState<string>('')
  const [sidebarAppliedFilters, setSidebarAppliedFilters] = useState<{
    filterBy: string
    dateFrom: string
    dateTo: string
    category: string
    item: string
  }>({
    filterBy: '',
    dateFrom: '',
    dateTo: '',
    category: '',
    item: '',
  })
  const [viewModeInitialFilterResolved, setViewModeInitialFilterResolved] =
    useState<boolean>(true)
  const [showContextNotesSidebar, setShowContextNotesSidebar] =
    useState<boolean>(() => openContextNotesByDefault === true)
  const lastContextFetchKeyRef = useRef<string>('')
  const inFlightContextFetchKeyRef = useRef<string | null>(null)
  const toolbarRef = useRef<RichTextEditorComponent>(null)
  const richTextEditorContainerRef = useRef<HTMLDivElement>(null)
  const contextNotesToggleWrapRef = useRef<HTMLDivElement | null>(null)
  const quickNoteInputRef = useRef<HTMLInputElement>(null)
  const signatureRef = useRef<SignatureComponent>(null)
  const tagDropdownRef = useRef<HTMLDivElement>(null)
  const aiNoteStructureDropdownRef = useRef<HTMLDivElement>(null)
  const aiToneDropdownRef = useRef<HTMLDivElement>(null)
  const happyPopoverRef = useRef<HTMLDivElement>(null)
  const mentionPopoverRef = useRef<HTMLDivElement>(null)
  const variablePopoverRef = useRef<HTMLDivElement>(null)
  const savedEmojiSelectionRef = useRef<{ restore: () => Range } | null>(null)
  // This is for unmounting and remounting of RTE
  const [rteEditKey, setRteEditKey] = useState(0)
  const prevViewModeRef = useRef(false)
  const originalEditRef = useRef<{
    noteId: string
    title: string
    content: string
  } | null>(null)
  const didFocusEditorRef = useRef(false)

  const [showHappyPopover, setShowHappyPopover] = useState(false)
  const [showMentionPopover, setShowMentionPopover] = useState(false)
  const [showVariablePopover, setShowVariablePopover] = useState(false)
  const [happyPopoverPosition, setHappyPopoverPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const [mentionPopoverPosition, setMentionPopoverPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const [variablePopoverPosition, setVariablePopoverPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const persistableTranscriptRef = useRef<INoteTranscriptSegment[]>([])
  const transcriptSummarySegmentsRef = useRef<INoteTranscriptSegment[]>([])
  const transcriptSummaryRequestIdRef = useRef(0)
  const lastNotesAiGenerationRef = useRef<LastNotesAiGeneration | null>(null)
  const aiCommittedToNoteRef = useRef(false)
  const pendingAutoReplaceInsertedAiRef = useRef(false)

  const patients = useAppSelector((state) => state.patient.data)
  const storeNotes = useAppSelector((state) => state.note.data)
  const storeNotesLoading = useAppSelector((state) => state.note.loading)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const loggedInUser = useAppSelector(
    (state) => state.chatInternal.loggedInUser,
  )
  const doctorName = useMemo(
    () => buildDoctorDisplayName(loggedInUser),
    [loggedInUser],
  )

  const liveTranscribe = useNotesLiveTranscribe({
    doctorName,
    patientName,
    patientId,
    clinicId: selectedClinic?.clinic_id,
    onSegmentsChange: (panelSegments, persistable) => {
      persistableTranscriptRef.current = persistable
      onTranscriptSegmentsChange?.(panelSegments, persistable)
    },
  })

  const noteVariableList = useMemo(
    () =>
      buildNoteVariableList({
        clinic: selectedClinic ?? undefined,
        patientName,
      }),
    [selectedClinic, patientName],
  )

  const aiNoteStructureOptions = useMemo(() => {
    const builtins = [
      { label: 'Default', value: 'default' },
      { label: 'SOAP', value: 'soap' },
    ]
    const templateOptions = customTemplates
      .slice()
      .sort((a, b) => {
        const categoryCompare = (a.category || 'Custom').localeCompare(
          b.category || 'Custom',
        )
        if (categoryCompare !== 0) return categoryCompare
        return a.name.localeCompare(b.name)
      })
      .map((template) => ({
        label: template.category
          ? `${template.category} - ${template.name}`
          : template.name,
        value: template.id,
      }))
    return [...builtins, ...templateOptions]
  }, [customTemplates])

  const aiToneOptions = useMemo(
    () => [
      { label: 'Brief', value: 'brief' },
      { label: 'Detailed', value: 'detailed' },
      { label: 'Patient-friendly', value: 'patientFriendly' },
      { label: 'Professional', value: 'professional' },
    ],
    [],
  )

  const mentionOptions = useMemo(() => {
    return patients
      .map((patient) => getFullName(patient))
      .filter((name): name is string => !!name && name.trim().length > 0)
  }, [patients])

  const getToolbarButtonPosition = useCallback((selector: string) => {
    const button = document.querySelector(selector) as HTMLElement | null
    if (!button) return null
    const rect = button.getBoundingClientRect()
    return {
      top: rect.bottom + window.scrollY + 8,
      left: rect.left + window.scrollX,
    }
  }, [])

  const {
    aiResponse,
    showAiActions,
    showEnhanceDropdown,
    generateResponse,
    enhanceResponse,
    insertResponse,
    replaceResponse,
    resetAIState,
    setShowEnhanceDropdown,
    isLoading: isAiLoading,
    error: aiError,
  } = useAIResponse()
  const isViewMode = displayedMode === 'view'

  // After Insert/Replace, Structure/Tone changes should refresh the note body AI block.
  useEffect(() => {
    if (!pendingAutoReplaceInsertedAiRef.current) return
    if (isAiLoading) return
    if (aiError) {
      pendingAutoReplaceInsertedAiRef.current = false
      return
    }
    if (!aiResponse.trim()) return
    pendingAutoReplaceInsertedAiRef.current = false
    setNoteContent((prev) => replaceInsertedAiResponseBlock(prev, aiResponse))
  }, [aiError, aiResponse, isAiLoading, setNoteContent])

  const versionSiblingPool = useMemo((): INote[] => {
    if (hasContextNotesOwnership && contextNotes.length > 0) {
      return contextNotes
    }
    return storeNotes ?? []
  }, [hasContextNotesOwnership, contextNotes, storeNotes])

  const canEditDisplayedNoteVersion = useMemo((): boolean => {
    const n = displayedNote
    if (!n?.note_id?.trim()) return true
    return isLatestNoteInVersionChain(n, versionSiblingPool)
  }, [displayedNote, versionSiblingPool])

  const handleRteChange = useCallback(
    (args: { value: string }): void => {
      setNoteContent(args.value)
    },
    [setNoteContent],
  )

  const hasAiResponse = aiResponse.trim().length > 0
  const canUseNotesTextAi = Boolean(selectedClinic?.clinic_id)
  const canCommitAiResponse = hasAiResponse && !isAiLoading
  const shouldShowAiPreview = showAiActions
  const hasQuickNoteText = quickNote.trim().length > 0
  const {
    isRecording,
    isRecordingPaused,
    recordingTakes,
    activeTakeIndex,
    activeMic,
    setActiveMic,
    showResetRecordingModal,
    flashModeTestUrl,
    flashModeAudioRef,
    isSmartMode,
    isFlashMode,
    isLiveTranscribeConnecting,
    isUpdatingTranscription,
    isRecordingActive,
    shouldShowAiRecordingBar,
    hasRecordedAudio,
    audioUrl,
    shouldShowAiPlaybackControls,
    showRecordingTakeNav,
    isFlashModeTesting,
    micStatusLabel,
    micModeLabel,
    generateTranscriptSummaryRef,
    handleMicClick,
    handleRecordingCircleClick,
    handleRecordingStop,
    handleRecordingPause,
    handleResetRecordingRequest,
    handleConfirmResetRecording,
    handleCancelResetRecording,
    handlePreviousRecordingTake,
    handleNextRecordingTake,
    handleRecordingTakeEnded,
  } = useNoteEditorRecording({
    liveTranscribe,
    onViewTranscription,
    onTranscriptSegmentsChange,
    resetAIState,
    setAiPreviewSource,
    persistableTranscriptRef,
    transcriptSummaryRequestIdRef,
    transcriptSummarySegmentsRef,
    lastNotesAiGenerationRef,
    aiCommittedToNoteRef,
    pendingAutoReplaceInsertedAiRef,
    noteId: note?.note_id ?? null,
    savedTranscriptSegments: note?.transcript_segments,
  })
  const notesTextAiDisabledTitle = canUseNotesTextAi
    ? undefined
    : NOTES_AI_SELECT_CLINIC_TITLE

  /** When true, we are waiting to select the first note (View note from Charting or Contact); show loading instead of create form to avoid flash. */
  const selectFirstNotePending =
    Boolean(selectFirstNote) &&
    (hasContextId || isContactContext) &&
    !contextNotesFetched
  const shouldDelaySidebarListForInitialFilter =
    Boolean(selectFirstNote) &&
    (hasContextId || isContactContext) &&
    !selectFirstNoteResolved
  const sidebarFilteredNotes = useMemo(
    (): INote[] =>
      applySidebarFiltersToNotes(
        contextNotes,
        sidebarAppliedFilters,
        sidebarSearchQuery,
      ),
    [contextNotes, sidebarAppliedFilters, sidebarSearchQuery],
  )
  const sortedSidebarFilteredNotes = useMemo(
    (): INote[] =>
      [...sidebarFilteredNotes].sort((a, b) => {
        const aTime = new Date(
          a.date_time_updated_at ?? a.date_time_created_at ?? 0,
        ).getTime()
        const bTime = new Date(
          b.date_time_updated_at ?? b.date_time_created_at ?? 0,
        ).getTime()
        return bTime - aTime
      }),
    [sidebarFilteredNotes],
  )
  const shouldDelaySidebarListForViewAutoFilter =
    autoFilterOnViewSelect &&
    mode === 'view' &&
    !hasContextId &&
    !isContactContext &&
    !isCallContext &&
    !viewModeInitialFilterResolved
  const shouldDelaySidebarListRender =
    shouldDelaySidebarListForInitialFilter ||
    shouldDelaySidebarListForViewAutoFilter

  const fetchContextNotes = useCallback(async (): Promise<void> => {
    const fetchKey =
      isCallContext && contextId?.trim()
        ? `call:${contextId.trim()}`
        : isContactContext && contactIdProp?.trim()
          ? `contact:${contactIdProp.trim()}`
          : patientId
            ? `patient:${patientId}`
            : ''

    if (
      fetchKey.length > 0 &&
      inFlightContextFetchKeyRef.current != null &&
      inFlightContextFetchKeyRef.current === fetchKey
    ) {
      return
    }
    if (fetchKey.length > 0) {
      inFlightContextFetchKeyRef.current = fetchKey
    }
    if (isCallContext && contextId?.trim()) {
      try {
        const notes = await dispatch(
          fetchAllNotes({
            patientId: undefined,
            contactId: undefined,
            contextType: NoteContextType.CALL,
            contextId: contextId.trim(),
          }),
        ).unwrap()
        const list = notes ?? []
        setContextNotes(list)
        setContextNotesFetched(true)
      } catch (err) {
        logger.error('Error fetching sidebar notes for call:', err)
        setContextNotes([])
        setContextNotesFetched(true)
      } finally {
        inFlightContextFetchKeyRef.current = null
      }
      return
    }
    if (isContactContext && contactIdProp?.trim()) {
      try {
        const notes = await dispatch(
          fetchAllNotes({
            patientId: undefined,
            contactId: contactIdProp.trim(),
            contextType: NoteContextType.CONTACT,
            contextId: undefined,
          }),
        ).unwrap()
        const list = notes ?? []
        setContextNotes(list)
        setContextNotesFetched(true)
      } catch (err) {
        logger.error('Error fetching sidebar notes:', err)
        setContextNotes([])
        setContextNotesFetched(true)
      } finally {
        inFlightContextFetchKeyRef.current = null
      }
      return
    }
    if (patientId) {
      try {
        const notes = await dispatch(
          fetchAllNotes({
            patientId,
            contactId: undefined,
            contextType: undefined,
            contextId: undefined,
          }),
        ).unwrap()
        const list = notes ?? []
        setContextNotes(list)
        setContextNotesFetched(true)
      } catch (err) {
        logger.error('Error fetching sidebar notes:', err)
        setContextNotes([])
        setContextNotesFetched(true)
      } finally {
        inFlightContextFetchKeyRef.current = null
      }
      return
    }
    setContextNotes([])
    setContextNotesFetched(true)
    inFlightContextFetchKeyRef.current = null
  }, [
    dispatch,
    patientId,
    isContactContext,
    contactIdProp,
    isCallContext,
    contextId,
  ])

  const canShowNotesSidebar =
    Boolean(patientId) || Boolean(isContactContext) || Boolean(isCallContext)

  /** When true, the notes list (sidebar at the back) is still loading. */
  const isSidebarNotesLoading = canShowNotesSidebar && !contextNotesFetched

  /** When true, hide body row and show blank. Wait for sidebar list and for first-note selection (Charting/Contact view) so RTE never flashes before view mode. */
  const isBodyRowLoading = isSidebarNotesLoading || selectFirstNotePending
  const currentViewNoteId = displayedNote?.note_id ?? note?.note_id ?? null
  const currentViewNoteContextType = (
    displayedNote?.context_type ??
    note?.context_type ??
    ''
  ).toString()
  const currentViewNoteContextId = displayedNote
    ? getNoteContextItemId(displayedNote)
    : note
      ? getNoteContextItemId(note)
      : ''

  // When only patientId is set (Notes page flow), use notes from Redux so we don't duplicate the page's fetch.
  useEffect(() => {
    if (patientId && !isCallContext && !isContactContext) {
      if (storeNotesLoading) {
        setContextNotesFetched(false)
        return
      }
      const list = Array.isArray(storeNotes)
        ? (storeNotes as INote[]).filter((n) => n.patient_id === patientId)
        : []
      setContextNotes(list)
      setContextNotesFetched(true)
    }
  }, [
    patientId,
    isCallContext,
    isContactContext,
    storeNotes,
    storeNotesLoading,
  ])

  // When call or contact context: fetch notes. Do not depend on storeNotes so store updates after fetch don't re-trigger (infinite loop).
  useEffect(() => {
    if (isCallContext || isContactContext) {
      const contextFetchKey = isCallContext
        ? `call:${contextId ?? ''}`
        : `contact:${contactIdProp ?? ''}`
      if (
        contextFetchKey.length > 0 &&
        lastContextFetchKeyRef.current === contextFetchKey
      ) {
        return
      }
      lastContextFetchKeyRef.current = contextFetchKey
      setContextNotesFetched(false)
      void fetchContextNotes()
      return
    }
    lastContextFetchKeyRef.current = ''
    if (!patientId) {
      setContextNotes([])
      setContextNotesFetched(hasContextId)
    }
  }, [
    patientId,
    isCallContext,
    isContactContext,
    hasContextId,
    contactIdProp,
    contextId,
    selectFirstNote,
    fetchContextNotes,
  ])

  // When opening from Charting History (or any context), pre-fill sidebar filter so Category and Item show the selected context.
  useEffect(() => {
    if (
      hasContextId &&
      contextId?.trim() &&
      contextType !== NoteContextType.CONTACT
    ) {
      setSidebarAppliedFilters((prev) => ({
        ...prev,
        category: (contextType ?? '').toString(),
        item: contextId.trim(),
      }))
    }
  }, [hasContextId, contextId, contextType])

  // When opening from Contact page (Add note): pre-fill Category with "contact", no options for Item.
  useEffect(() => {
    if (isContactContext) {
      setSidebarAppliedFilters((prev) => ({
        ...prev,
        category: NoteContextType.CONTACT,
        item: '',
      }))
    }
  }, [isContactContext])

  useEffect(() => {
    if (openContextNotesByDefault && canShowNotesSidebar) {
      setShowContextNotesSidebar(true)
    }
  }, [openContextNotesByDefault, canShowNotesSidebar])

  useEffect(() => {
    if (!selectFirstNote || !contextNotesFetched) return
    if (!(hasContextId || isContactContext)) return

    const expectedContextItem = contextId?.trim() ?? ''
    if (
      hasContextId &&
      !isContactContext &&
      expectedContextItem &&
      sidebarAppliedFilters.item !== expectedContextItem
    ) {
      return
    }

    const nextFirstNote = isContactContext
      ? (sortedSidebarFilteredNotes.find(
          (n) => n.context_type === NoteContextType.CONTACT,
        ) ?? null)
      : (sortedSidebarFilteredNotes[0] ?? null)

    if (nextFirstNote) {
      const shouldSyncSelection =
        displayedNote?.note_id !== nextFirstNote.note_id ||
        displayedMode !== 'view'
      if (shouldSyncSelection) {
        setActiveNote(nextFirstNote)
        setActiveMode('view')
      }
    }
    setSelectFirstNoteResolved(true)
  }, [
    selectFirstNote,
    hasContextId,
    isContactContext,
    contextNotesFetched,
    contextId,
    sidebarAppliedFilters.item,
    sortedSidebarFilteredNotes,
    displayedNote?.note_id,
    displayedMode,
  ])

  const hasEmittedInitialContextResolvedRef = useRef(false)
  useEffect(() => {
    if (selectFirstNote) {
      return
    }
    setSelectFirstNoteResolved(false)
    hasEmittedInitialContextResolvedRef.current = false
  }, [selectFirstNote])
  useEffect(() => {
    if (!onInitialContextNoteResolved) return
    if (!(hasContextId || isContactContext)) return
    if (!selectFirstNoteResolved) return
    if (hasEmittedInitialContextResolvedRef.current) return
    hasEmittedInitialContextResolvedRef.current = true
    onInitialContextNoteResolved()
  }, [
    onInitialContextNoteResolved,
    hasContextId,
    isContactContext,
    selectFirstNoteResolved,
  ])

  useEffect(() => {
    if (!canShowNotesSidebar) return
    const container = richTextEditorContainerRef.current
    if (!container) return

    const DATA_ATTR = 'data-context-notes-toggle'
    const removeInjectedButton = (): void => {
      if (contextNotesToggleWrapRef.current) {
        contextNotesToggleWrapRef.current.remove()
        contextNotesToggleWrapRef.current = null
      }
    }

    const timeoutId = setTimeout(() => {
      const toolbar = container.querySelector(
        '.e-rte-toolbar',
      ) as HTMLElement | null
      if (!toolbar) return
      removeInjectedButton()
      const wrap = document.createElement('div')
      wrap.setAttribute(DATA_ATTR, 'true')
      wrap.style.cssText =
        'display:inline-flex;align-items:center;margin-left:auto;padding:0 6px;position: absolute;right: 0;top: 50%;transform: translateY(-50%);'
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'e-btn e-flat e-small context-notes-toggle-btn'
      btn.style.cssText =
        'min-width:32px;height:28px;display:inline-flex;align-items:center;justify-content:center;'
      const updateButtonLabel = (isShown: boolean): void => {
        btn.title = isShown ? 'Hide notes list' : 'Show notes list'
        btn.setAttribute('aria-label', btn.title)
        btn.innerHTML = isShown
          ? '<i class="mgc mgc_layout_rightbar_close_line" style="font-size:16px;"></i>'
          : '<i class="mgc mgc_layout_rightbar_open_line" style="font-size:16px;"></i>'
      }
      updateButtonLabel(showContextNotesSidebar)
      btn.addEventListener('click', () => {
        setShowContextNotesSidebar((prev) => !prev)
      })
      wrap.appendChild(btn)
      toolbar.appendChild(wrap)
      contextNotesToggleWrapRef.current = wrap
    }, 150)

    return () => {
      clearTimeout(timeoutId)
      removeInjectedButton()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit showContextNotesSidebar so we don't remove/recreate button on toggle (avoids stuck tooltip)
  }, [canShowNotesSidebar])

  useEffect(() => {
    const wrap = contextNotesToggleWrapRef.current
    if (!wrap) return
    const btn = wrap.querySelector('button')
    if (!btn) return
    const isShown = showContextNotesSidebar
    btn.title = isShown ? 'Hide notes list' : 'Show notes list'
    btn.setAttribute('aria-label', btn.title)
    btn.innerHTML = isShown
      ? '<i class="mgc mgc_layout_rightbar_close_line" style="font-size:16px;"></i>'
      : '<i class="mgc mgc_layout_rightbar_open_line" style="font-size:16px;"></i>'
  }, [showContextNotesSidebar])

  /**
   * Close Syncfusion RTE toolbar inline dropdowns that can stay visible on view switches/blur.
   * We hide via popup state classes instead of removing nodes to avoid desync with Syncfusion internals.
   */
  const closeRteInlineDropdownPopups = useCallback(
    (scopeContainer?: HTMLElement | null): void => {
      const popups = Array.from(
        document.querySelectorAll<HTMLElement>(
          '.e-dropdown-popup.e-rte-elements.e-rte-inline-dropdown',
        ),
      )
      if (popups.length === 0) return

      const activeContainer = scopeContainer ?? null
      const popupBelongsToContainer = (popup: HTMLElement): boolean => {
        if (!activeContainer) return true
        const popupId = popup.id
        if (!popupId) return false
        const owner = document.querySelector<HTMLElement>(
          `[aria-controls="${popupId}"], [aria-owns="${popupId}"]`,
        )
        if (!owner) return false
        return activeContainer.contains(owner)
      }

      // Ask Syncfusion to close first, then enforce hidden state for any stale popup.
      document.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }),
      )
      requestAnimationFrame(() => {
        popups.forEach((popup) => {
          if (!popupBelongsToContainer(popup)) return
          popup.classList.remove('e-popup-open')
          popup.classList.add('e-popup-close')
          popup.setAttribute('aria-hidden', 'true')
          // Keep style cleanup minimal so Syncfusion can re-open/reuse popup elements.
          popup.style.removeProperty('display')
          popup.style.removeProperty('visibility')
          popup.style.removeProperty('opacity')
          popup.style.removeProperty('pointer-events')
        })
      })
    },
    [],
  )

  // Destroy Syncfusion RTE instance when dropdown closes after unmount/navigation
  const destroyRteIfMounted = useCallback(() => {
    const rte = toolbarRef.current as { destroy?: () => void } | null
    if (rte && typeof rte.destroy === 'function') {
      try {
        rte.destroy()
      } catch {
        console.error('Error destroying RTE instance')
      }
    }
  }, [])

  useEffect(() => {
    return () => {
      destroyRteIfMounted()
      cleanupSyncfusionOrphanedTooltips()
    }
  }, [destroyRteIfMounted])

  useLayoutEffect(() => {
    if (prevViewModeRef.current && !isViewMode) {
      setRteEditKey((k) => k + 1)
    }
    prevViewModeRef.current = isViewMode
  }, [isViewMode])

  useEffect(() => {
    if (isViewMode) setTimeout(closeRteInlineDropdownPopups, 0)
    cleanupSyncfusionOrphanedTooltips()
    return () => {
      setTimeout(closeRteInlineDropdownPopups, 0)
      cleanupSyncfusionOrphanedTooltips()
    }
  }, [isViewMode, closeRteInlineDropdownPopups])

  useEffect(() => {
    if (isViewMode) return
    const container = richTextEditorContainerRef.current
    if (!container) return
    const handleDocClick = (e: MouseEvent): void => {
      const target = e.target as Node
      const targetEl = target instanceof Element ? target : target.parentElement
      if (targetEl?.closest('.notes-save-confirm')) {
        return
      }
      const popups = Array.from(
        document.querySelectorAll<HTMLElement>(
          '.e-dropdown-popup.e-rte-inline-dropdown',
        ),
      )
      const clickedInsidePopup = popups.some((popup) => popup.contains(target))
      const clickedInsideEditor = container.contains(target)
      if (!clickedInsidePopup && !clickedInsideEditor) {
        closeRteInlineDropdownPopups(container)
      }
    }
    document.addEventListener('mousedown', handleDocClick, true)
    return () => document.removeEventListener('mousedown', handleDocClick, true)
  }, [isViewMode, closeRteInlineDropdownPopups])

  const effectiveOnSave = useCallback(
    (payload: {
      id?: string
      title: string
      content: string
      tag: string
      patientId?: string
      contextId?: string
    }): void | Promise<INote | undefined> => {
      if (onSave) return onSave(payload)
      if (hasContextId && patientId) {
        return saveNoteAction(
          dispatch,
          {
            noteData: payload,
            patientId,
            contextId: contextId ?? null,
            contextType: contextType ?? NoteContextType.PATIENT,
            siblingNotes: versionSiblingPool,
          },
          {
            onSuccess: (t, m) => showToast(createSuccessToast(t, m)),
            onError: (t, m) => showToast(createFailedToast(t, m)),
          },
        )
      }
      showToast(
        createFailedToast(
          'Save note failed',
          'Please select a patient before saving.',
        ),
      )
      return undefined
    },
    [
      onSave,
      hasContextId,
      patientId,
      contextId,
      contextType,
      dispatch,
      showToast,
      versionSiblingPool,
    ],
  )

  useEffect(() => {
    if (!initialNewNoteContextType) {
      return
    }
    setNewNoteSeedContext({
      contextType: initialNewNoteContextType,
      contextId: initialNewNoteContextId?.trim() || undefined,
    })
  }, [initialNewNoteContextType, initialNewNoteContextId])

  const effectiveOnDelete = useCallback(
    async (noteId: string): Promise<void> => {
      if (onDelete) {
        onDelete(noteId)
        return
      }
      if (hasContextId) {
        await deleteNoteAction(dispatch, noteId, {
          onSuccess: (t, m) => showToast(createSuccessToast(t, m)),
          onError: (t, m) => showToast(createFailedToast(t, m)),
        })
        setActiveNote(null)
        setActiveMode('edit')
        fetchContextNotes()
      }
    },
    [onDelete, hasContextId, dispatch, showToast, fetchContextNotes],
  )

  useEffect(() => {
    if (
      !hasContextId ||
      !patientId ||
      !contextNotes.some(
        (n) =>
          (n.context_type ?? '').toString() === NoteContextType.APPOINTMENT ||
          (n.context_type ?? '').toString() === 'appointment',
      )
    ) {
      return
    }
    let cancelled = false
    getAppointmentsByPatientId(patientId)
      .then((response) => {
        if (cancelled) return
        const list = response?.data?.appointments ?? []
        setAppointmentsForFilter(
          list.map((a) => ({
            appointment_id: a.appointment_id,
            appt_date: a.appt_date,
            appt_start: a.appt_start,
            appointment_start_date: a.appointment_start_date,
          })),
        )
      })
      .catch(() => {
        if (!cancelled) setAppointmentsForFilter([])
      })
    return () => {
      cancelled = true
    }
  }, [hasContextId, patientId, contextNotes])

  const sidebarApplyFilters = useCallback(
    (filters: {
      filterBy: string
      dateFrom: string
      dateTo: string
      category: string
      item: string
    }): void => {
      setSidebarAppliedFilters(filters)
    },
    [],
  )

  // In view mode (Notes page — no explicit contextId/contact/call): auto-scope
  // the sidebar list to the viewed note's context, matching the charting flow.
  const viewModeAppliedNoteIdRef = useRef<string | null>(null)
  useEffect(() => {
    const shouldAutoScopeViewNote =
      autoFilterOnViewSelect &&
      mode === 'view' &&
      !hasContextId &&
      !isContactContext &&
      !isCallContext
    if (!shouldAutoScopeViewNote) {
      setViewModeInitialFilterResolved(true)
      viewModeAppliedNoteIdRef.current = null
      return
    }
    if (!currentViewNoteId || !currentViewNoteContextType) {
      setViewModeInitialFilterResolved(false)
      return
    }
    if (viewModeAppliedNoteIdRef.current === currentViewNoteId) {
      setViewModeInitialFilterResolved(true)
      return
    }

    setViewModeInitialFilterResolved(false)
    viewModeAppliedNoteIdRef.current = currentViewNoteId
    sidebarApplyFilters({
      filterBy: '',
      dateFrom: '',
      dateTo: '',
      category: currentViewNoteContextType,
      item: currentViewNoteContextId,
    })
    setViewModeInitialFilterResolved(true)
  }, [
    autoFilterOnViewSelect,
    mode,
    hasContextId,
    isContactContext,
    isCallContext,
    currentViewNoteId,
    currentViewNoteContextType,
    currentViewNoteContextId,
    sidebarApplyFilters,
  ])

  // Sync local state from displayed note when it changes so view/editor
  // always shows latest note (e.g. after save or selecting from sidebar).
  // When selectFirstNote is pending, skip syncing empty content to avoid
  // setNoteContent('') then setNoteContent(firstNote.body) in quick succession (cursor flicker).
  useEffect(() => {
    if (displayedNote) {
      setNewNoteSeedContext(null)
      setNoteTitle(displayedNote.title || 'New Note')
      setNoteContent(displayedNote.body ?? '')
      setSelectedTag(
        (displayedNote.context_type as NoteContextType) ||
          NoteContextType.PATIENT,
      )
    } else {
      if (
        selectFirstNote &&
        (hasContextId || isContactContext) &&
        !selectFirstNoteResolved
      ) {
        return
      }
      setNoteTitle(initialTitle || 'New Note')
      setNoteContent('')
      const seededContextType = newNoteSeedContext?.contextType
      setSelectedTag(
        tagLockedToContext
          ? (contextType ?? seededContextType ?? NoteContextType.PATIENT)
          : (seededContextType ?? NoteContextType.PATIENT),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when displayed note id/mode or (for new note) context lock/type
  }, [
    displayedNote?.note_id,
    displayedMode,
    tagLockedToContext,
    contextType,
    newNoteSeedContext,
  ])

  // Reset one-time focus flag when switching to another note so the next note gets one focus when ready.
  useEffect(() => {
    didFocusEditorRef.current = false
  }, [displayedNote?.note_id])

  // One-time focus when editor is ready (edit mode, RTE mounted) to avoid cursor flicker from repeated focus.
  useEffect(() => {
    if (displayedMode !== 'edit' || didFocusEditorRef.current) return
    const id = requestAnimationFrame(() => {
      if (didFocusEditorRef.current) return
      if (toolbarRef.current) {
        toolbarRef.current.focusIn()
        didFocusEditorRef.current = true
      }
    })
    return () => cancelAnimationFrame(id)
  }, [displayedMode, displayedNote?.note_id])

  useEffect(() => {
    if (displayedMode !== 'edit') return
    const container = richTextEditorContainerRef.current
    if (!container) return

    const syncRteContent = (): void => {
      const rte = toolbarRef.current as { getHtml?: () => string } | null
      if (!rte || typeof rte.getHtml !== 'function') return
      try {
        const latestHtml = rte.getHtml()
        setNoteContent((prev) => (prev === latestHtml ? prev : latestHtml))
      } catch {
        // Syncfusion can briefly detach its content module while remounting.
      }
    }

    const attachInputListener = (): (() => void) | undefined => {
      const content = container.querySelector<HTMLElement>(
        '.e-richtexteditor .e-rte-content .e-content',
      )
      if (!content) return undefined
      content.addEventListener('input', syncRteContent)
      content.addEventListener('keyup', syncRteContent)
      content.addEventListener('paste', syncRteContent)
      return () => {
        content.removeEventListener('input', syncRteContent)
        content.removeEventListener('keyup', syncRteContent)
        content.removeEventListener('paste', syncRteContent)
      }
    }

    let detach = attachInputListener()
    if (detach) return detach

    const timeoutId = window.setTimeout(() => {
      detach = attachInputListener()
    }, 100)

    return () => {
      window.clearTimeout(timeoutId)
      detach?.()
    }
  }, [displayedMode, displayedNote?.note_id, rteEditKey, canShowNotesSidebar])

  // Track original title/content when opening a note so we can detect unsaved edits.
  useEffect(() => {
    if (displayedNote) {
      originalEditRef.current = {
        noteId: displayedNote.note_id,
        title: displayedNote.title || 'New Note',
        content: displayedNote.body ?? '',
      }
    } else {
      originalEditRef.current = null
    }
  }, [displayedNote])

  /** When tagLockedToContext (e.g. Charting), save uses contextType; otherwise uses selectedTag (Notes page). */
  const effectiveTag = useMemo(
    () =>
      tagLockedToContext
        ? (contextType ?? NoteContextType.PATIENT)
        : selectedTag,
    [tagLockedToContext, contextType, selectedTag],
  )
  const effectiveContextId = useMemo((): string | undefined => {
    if (displayedNote) {
      return undefined
    }
    const seedContextId = newNoteSeedContext?.contextId?.trim()
    return seedContextId || undefined
  }, [displayedNote, newNoteSeedContext])

  // In view mode, resolve img[data-blob] to read-only URLs so images display after save or when reopening.
  useEffect(() => {
    if (!isViewMode || !noteContent || !noteContent.includes('data-blob')) {
      setResolvedContentForView(null)
      return
    }
    let cancelled = false
    resolveNoteBodyBlobSrc(noteContent)
      .then((resolved) => {
        if (!cancelled) setResolvedContentForView(resolved)
      })
      .catch(() => {
        if (!cancelled) setResolvedContentForView(null)
      })
    return () => {
      cancelled = true
    }
  }, [isViewMode, noteContent])

  // In edit mode, resolve img[data-blob] when opening a note so the editor shows working image URLs.
  useEffect(() => {
    if (
      isViewMode ||
      !displayedNote?.body ||
      !displayedNote.body.includes('data-blob')
    ) {
      return
    }
    let cancelled = false
    const noteId = displayedNote.note_id
    resolveNoteBodyBlobSrc(displayedNote.body)
      .then((resolved) => {
        if (!cancelled) {
          setNoteContent(resolved)
          if (originalEditRef.current?.noteId === noteId) {
            originalEditRef.current = {
              ...originalEditRef.current,
              content: resolved,
            }
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          // keep existing noteContent on resolve failure
        }
      })
    return () => {
      cancelled = true
    }
  }, [isViewMode, displayedNote?.note_id, displayedNote?.body])

  // In view mode, substitute variable placeholders with their values for display.
  const viewDisplayContent = useMemo((): string => {
    if (!isViewMode) return ''
    const base = resolvedContentForView ?? noteContent ?? ''
    let out = base
    noteVariableList.forEach((v) => {
      out = out.replaceAll(v.text, v.value)
    })
    return out
  }, [isViewMode, resolvedContentForView, noteContent, noteVariableList])

  const aiNoteStructureLabel = useMemo(() => {
    const selectedOption = aiNoteStructureOptions.find(
      (option) => option.value === aiNoteStructure,
    )
    if (selectedOption) {
      return selectedOption.label
    }
    return 'Note structure'
  }, [aiNoteStructure, aiNoteStructureOptions])
  const aiToneLabel = useMemo(() => {
    const selectedOption = aiToneOptions.find(
      (option) => option.value === aiTone,
    )
    if (selectedOption) {
      return selectedOption.label
    }
    return 'Tone'
  }, [aiTone, aiToneOptions])

  const getAiOptionLabel = useCallback(
    (
      options: Array<{ label: string; value: string }>,
      value: string,
    ): string | undefined => {
      if (!value.trim()) return undefined
      return options.find((option) => option.value === value)?.label || value
    },
    [],
  )

  const resolveAiStructureTemplate = useCallback(
    (structureValue: string): { html?: string; outline?: string } => {
      if (
        !structureValue ||
        structureValue === 'default' ||
        structureValue === 'soap'
      ) {
        return {}
      }
      const template = customTemplates.find(
        (item) => item.id === structureValue,
      )
      if (!template?.content) return {}
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      const html = template.content
        .replace(/\{\{patientName\}\}/g, patientName)
        .replace(/\{\{currentDate\}\}/g, currentDate)
      return {
        html,
        outline: htmlTemplateToMarkdownOutline(html) || undefined,
      }
    },
    [customTemplates, patientName],
  )

  const generateTranscriptSummary = useCallback(
    async (
      segments: INoteTranscriptSegment[],
      overrides?: { structureValue?: string; toneValue?: string },
    ): Promise<void> => {
      if (!canUseNotesTextAi) return

      const query = buildTranscriptSummaryInput(segments, {
        existingNoteHtml: noteContent,
      })
      if (!query) return

      const requestId = ++transcriptSummaryRequestIdRef.current
      const structureValue = overrides?.structureValue ?? aiNoteStructure
      const toneValue = overrides?.toneValue ?? aiTone
      transcriptSummarySegmentsRef.current = segments.map((segment) => ({
        ...segment,
      }))
      lastNotesAiGenerationRef.current = {
        source: 'transcript-summary',
        segments: transcriptSummarySegmentsRef.current,
        query,
        featureKey: 'custom',
        label: 'Transcript summary',
      }
      setAiPreviewSource('transcript-summary')
      setShowAiOverlay(true)

      const { html: structureHtml, outline: structureOutline } =
        resolveAiStructureTemplate(structureValue)
      const useTemplateFill = Boolean(structureHtml)

      await generateResponse(query, {
        label: 'Transcript summary',
        featureKey: 'custom',
        structure: getAiOptionLabel(aiNoteStructureOptions, structureValue),
        structureHtml,
        structureOutline,
        tone: getAiOptionLabel(aiToneOptions, toneValue),
        promptTemplate: useTemplateFill
          ? NOTES_TEMPLATE_FILL_PROMPT_TEMPLATE
          : NOTES_TRANSCRIPT_SUMMARY_PROMPT_TEMPLATE,
        systemPrompt: useTemplateFill
          ? NOTES_TEMPLATE_FILL_SYSTEM_PROMPT
          : NOTES_TRANSCRIPT_SUMMARY_SYSTEM_PROMPT,
      })

      if (requestId !== transcriptSummaryRequestIdRef.current) return
    },
    [
      aiNoteStructure,
      aiNoteStructureOptions,
      aiTone,
      aiToneOptions,
      canUseNotesTextAi,
      generateResponse,
      getAiOptionLabel,
      noteContent,
      resolveAiStructureTemplate,
    ],
  )

  generateTranscriptSummaryRef.current = generateTranscriptSummary

  const regenerateFromRecordingBarOptions = useCallback(
    (overrides?: { structureValue?: string; toneValue?: string }): void => {
      if (!canUseNotesTextAi) return

      if (aiCommittedToNoteRef.current) {
        pendingAutoReplaceInsertedAiRef.current = true
      }

      const lastGeneration = lastNotesAiGenerationRef.current
      const segments =
        (lastGeneration?.source === 'transcript-summary' &&
        lastGeneration.segments?.length
          ? lastGeneration.segments
          : null) ||
        (transcriptSummarySegmentsRef.current.length > 0
          ? transcriptSummarySegmentsRef.current
          : null) ||
        (persistableTranscriptRef.current.length > 0
          ? persistableTranscriptRef.current
          : null) ||
        []

      if (segments.length > 0) {
        void generateTranscriptSummary(segments, overrides)
        return
      }

      const structureValue = overrides?.structureValue ?? aiNoteStructure
      const toneValue = overrides?.toneValue ?? aiTone
      const { html: structureHtml, outline: structureOutline } =
        resolveAiStructureTemplate(structureValue)

      if (lastGeneration?.source === 'quick-note' && lastGeneration.query) {
        const query = buildNotesAiUserSourceInput({
          existingNoteHtml: noteContent,
          transcriptSegments: persistableTranscriptRef.current,
          userRequest: lastGeneration.query,
        })
        setAiPreviewSource('quick-note')
        setShowAiOverlay(true)
        void generateResponse(query, {
          label: lastGeneration.label || lastGeneration.query,
          featureKey: lastGeneration.featureKey || 'custom',
          structure: getAiOptionLabel(aiNoteStructureOptions, structureValue),
          structureHtml,
          structureOutline,
          tone: getAiOptionLabel(aiToneOptions, toneValue),
        })
        return
      }

      // No transcript / prior ask: still regenerate from the current note draft.
      const noteOnlyQuery = buildNotesAiUserSourceInput({
        existingNoteHtml: noteContent,
      })
      if (!noteOnlyQuery) return

      lastNotesAiGenerationRef.current = {
        source: 'transcript-summary',
        segments: [],
        query: noteOnlyQuery,
        featureKey: 'custom',
        label: 'Note draft update',
      }
      setAiPreviewSource('transcript-summary')
      setShowAiOverlay(true)
      void generateResponse(noteOnlyQuery, {
        label: 'Note draft update',
        featureKey: 'custom',
        structure: getAiOptionLabel(aiNoteStructureOptions, structureValue),
        structureHtml,
        structureOutline,
        tone: getAiOptionLabel(aiToneOptions, toneValue),
        promptTemplate: structureHtml
          ? NOTES_TEMPLATE_FILL_PROMPT_TEMPLATE
          : NOTES_TRANSCRIPT_SUMMARY_PROMPT_TEMPLATE,
        systemPrompt: structureHtml
          ? NOTES_TEMPLATE_FILL_SYSTEM_PROMPT
          : NOTES_TRANSCRIPT_SUMMARY_SYSTEM_PROMPT,
      })
    },
    [
      aiNoteStructure,
      aiNoteStructureOptions,
      aiTone,
      aiToneOptions,
      canUseNotesTextAi,
      generateResponse,
      generateTranscriptSummary,
      getAiOptionLabel,
      noteContent,
      resolveAiStructureTemplate,
    ],
  )

  const handleAiNoteStructureSelect = useCallback(
    (value: string): void => {
      setAiNoteStructure(value)
      setShowAiNoteStructureDropdown(false)
      regenerateFromRecordingBarOptions({ structureValue: value })
    },
    [regenerateFromRecordingBarOptions],
  )

  const handleAiToneSelect = useCallback(
    (value: string): void => {
      setAiTone(value)
      setShowAiToneDropdown(false)
      regenerateFromRecordingBarOptions({ toneValue: value })
    },
    [regenerateFromRecordingBarOptions],
  )

  useEffect(() => {
    if (!shouldShowAiPlaybackControls && onHideTranscription) {
      onHideTranscription()
    }
  }, [shouldShowAiPlaybackControls, onHideTranscription])

  const isDraftDirty = useMemo((): boolean => {
    if (displayedMode !== 'edit' || displayedNote !== null) return false
    return noteTitle.trim() !== 'New Note' || !isNoteContentEmpty(noteContent)
  }, [displayedMode, displayedNote, noteTitle, noteContent])

  const isEditDirty = useMemo((): boolean => {
    if (
      displayedMode !== 'edit' ||
      !displayedNote ||
      !originalEditRef.current ||
      originalEditRef.current.noteId !== displayedNote.note_id
    ) {
      return false
    }
    if (hasContextId && activeNote === null) return false
    const orig = originalEditRef.current
    return noteTitle !== orig.title || noteContent !== orig.content
  }, [
    hasContextId,
    activeNote,
    displayedMode,
    displayedNote,
    noteTitle,
    noteContent,
  ])

  const hasUnsavedChanges = isDraftDirty || isEditDirty
  const shouldBlockNavigation = hasUnsavedChanges

  useEffect((): void => {
    onNavigationGuardChange?.(shouldBlockNavigation)
  }, [onNavigationGuardChange, shouldBlockNavigation])

  const handleTagClick = useCallback(() => {
    if (isViewMode || tagLockedToContext) return
    setShowTagDropdown(!showTagDropdown)
  }, [showTagDropdown, isViewMode, tagLockedToContext])

  const handleTagSelect = useCallback(
    (tagValue: string) => {
      if (isViewMode || tagLockedToContext) return
      setSelectedTag(tagValue as NoteContextType)
      setShowTagDropdown(false)
    },
    [isViewMode, tagLockedToContext],
  )

  const handleSave = useCallback(() => {
    if (!noteTitle.trim()) {
      alert('Please enter a note title')
      return
    }

    if (isNoteContentEmpty(noteContent)) {
      setEmptyNoteWarningAction('none')
      setShowEmptyNoteWarning(true)
      return
    }

    setShowSuccessModal(true)
  }, [noteTitle, noteContent])

  const handleConfirmSave = useCallback(async () => {
    if (!effectiveOnSave || isSavingNote) return
    let contentToSave = noteContent
    const rte = toolbarRef.current as { getHtml?: () => string } | null
    if (rte && typeof rte.getHtml === 'function') {
      try {
        const latestHtml = rte.getHtml()
        if (typeof latestHtml === 'string') {
          contentToSave = latestHtml
          setNoteContent(latestHtml)
        }
      } catch {
        // Keep React state if Syncfusion getHtml fails
      }
    }
    const payload = {
      id: displayedNote?.note_id,
      title: noteTitle,
      content: contentToSave,
      tag: effectiveTag,
      ...(patientId != null && patientId !== '' ? { patientId } : {}),
      ...(effectiveContextId ? { contextId: effectiveContextId } : {}),
      transcript_segments: persistableTranscriptRef.current,
    }
    setIsSavingNote(true)
    try {
      const result = effectiveOnSave(payload)
      const isPromise =
        result != null &&
        typeof (result as Promise<INote | undefined>).then === 'function'
      const saved = isPromise
        ? await (result as Promise<INote | undefined>)
        : (result as INote | undefined)
      if (saved) {
        originalEditRef.current = {
          noteId: saved.note_id,
          title: saved.title ?? noteTitle,
          content: saved.body ?? contentToSave,
        }
        setActiveNote(saved)
        setActiveMode('view')
        if (canShowNotesSidebar) {
          fetchContextNotes()
        }
        setShowSuccessModal(false)
      }
    } catch {
      showToast(
        createFailedToast(
          'Save note failed',
          'Could not save the note. Please try again.',
        ),
      )
    } finally {
      setIsSavingNote(false)
    }
  }, [
    displayedNote?.note_id,
    noteTitle,
    noteContent,
    effectiveTag,
    effectiveOnSave,
    canShowNotesSidebar,
    fetchContextNotes,
    patientId,
    effectiveContextId,
    isSavingNote,
    showToast,
  ])

  const doBack = useCallback((): void => {
    onBack()
    if (onToggleSidebar) {
      onToggleSidebar()
    }
  }, [onBack, onToggleSidebar])

  const handleBackClick = useCallback((): void => {
    if (hasUnsavedChanges) {
      setUnsavedModalType('back')
    } else {
      doBack()
    }
  }, [hasUnsavedChanges, doBack])

  const leaveEditorAfterCancel = useCallback((): void => {
    if (hasContextNotesOwnership) {
      if (!activeNote && sidebarFilteredNotes.length > 0) {
        setActiveNote(sidebarFilteredNotes[0])
      }
      setActiveMode('view')
      return
    }
    doBack()
  }, [hasContextNotesOwnership, activeNote, sidebarFilteredNotes, doBack])

  const handleCancelClick = useCallback((): void => {
    setUnsavedModalType('back')
  }, [])

  const handleBackDiscardAndLeave = useCallback((): void => {
    setUnsavedModalType(null)
    leaveEditorAfterCancel()
  }, [leaveEditorAfterCancel])

  const saveCurrentNoteForLeave = useCallback(async (): Promise<boolean> => {
    if (!noteTitle.trim()) {
      alert('Please enter a note title')
      return false
    }
    if (isNoteContentEmpty(noteContent)) {
      setEmptyNoteWarningAction(
        unsavedModalType === 'newNote' ? 'newNote' : 'leave',
      )
      setShowEmptyNoteWarning(true)
      setUnsavedModalType(null)
      return false
    }
    if (!effectiveOnSave) {
      return true
    }
    const payload = {
      id: displayedNote?.note_id,
      title: noteTitle,
      content: noteContent,
      tag: effectiveTag,
      ...(patientId != null && patientId !== '' ? { patientId } : {}),
      ...(effectiveContextId ? { contextId: effectiveContextId } : {}),
    }
    try {
      const result = effectiveOnSave(payload)
      const isPromise =
        result != null &&
        typeof (result as Promise<INote | undefined>).then === 'function'
      const saved = isPromise
        ? await (result as Promise<INote | undefined>)
        : (result as INote | undefined)
      if (saved) {
        return true
      }
      return false
    } catch {
      // Save failed; toast shown by save action; keep modal open
      return false
    }
  }, [
    noteTitle,
    noteContent,
    effectiveTag,
    displayedNote?.note_id,
    effectiveOnSave,
    patientId,
    effectiveContextId,
    unsavedModalType,
  ])

  const handleBackSaveAndLeave = useCallback(async (): Promise<void> => {
    const saved = await saveCurrentNoteForLeave()
    if (saved) {
      setUnsavedModalType(null)
      leaveEditorAfterCancel()
    }
  }, [saveCurrentNoteForLeave, leaveEditorAfterCancel])

  const resolveViewedNoteSeedContext =
    useCallback((): NewNoteSeedContext | null => {
      if (!displayedNote || displayedMode !== 'view') {
        if (!displayedNote) {
          return null
        }
      }
      const contextTypeValue = (displayedNote.context_type ?? '')
        .toString()
        .trim()
      if (!contextTypeValue) {
        return null
      }
      const nextContextType = contextTypeValue as NoteContextType
      const nextContextId = getNoteContextItemId(displayedNote).trim()
      return {
        contextType: nextContextType,
        contextId: nextContextId || undefined,
      }
    }, [displayedNote, displayedMode])

  const startNewContextNote = useCallback(
    (seedContext: NewNoteSeedContext | null): void => {
      setNewNoteSeedContext(seedContext)
      setActiveNote(null)
      setActiveMode('edit')
      setNoteTitle('New Note')
      setNoteContent('')
    },
    [],
  )

  const proceedToNewNote = useCallback((): void => {
    const seedContext = resolveViewedNoteSeedContext()
    setNewNoteSeedContext(seedContext)
    if (hasContextNotesOwnership) {
      startNewContextNote(seedContext)
    }
    if (onEditNote) {
      onEditNote(null)
      return
    }
  }, [
    onEditNote,
    hasContextNotesOwnership,
    startNewContextNote,
    resolveViewedNoteSeedContext,
  ])

  const handleNewNoteDiscardAndContinue = useCallback((): void => {
    setUnsavedModalType(null)
    proceedToNewNote()
  }, [proceedToNewNote])

  const handleNewNoteSaveAndContinue = useCallback(async (): Promise<void> => {
    const saved = await saveCurrentNoteForLeave()
    if (saved) {
      setUnsavedModalType(null)
      proceedToNewNote()
    }
  }, [saveCurrentNoteForLeave, proceedToNewNote])

  const handleEmptyNoteGotIt = useCallback((): void => {
    setShowEmptyNoteWarning(false)
    setEmptyNoteWarningAction('none')
  }, [])

  const handleEmptyNoteDiscard = useCallback((): void => {
    const action = emptyNoteWarningAction
    setShowEmptyNoteWarning(false)
    setEmptyNoteWarningAction('none')
    if (action === 'newNote') {
      proceedToNewNote()
      return
    }
    leaveEditorAfterCancel()
  }, [emptyNoteWarningAction, proceedToNewNote, leaveEditorAfterCancel])

  useEffect(() => {
    onRegisterSaveAndLeave?.(saveCurrentNoteForLeave)
    return () => {
      onRegisterSaveAndLeave?.(null)
    }
  }, [onRegisterSaveAndLeave, saveCurrentNoteForLeave])

  const handleUnsavedModalCancel = useCallback((): void => {
    if (unsavedModalType === 'openSidebarNote') {
      setPendingSidebarNote(null)
    }
    setUnsavedModalType(null)
  }, [unsavedModalType])

  const openPendingSidebarNote = useCallback(
    (item: INote): void => {
      if (hasContextNotesOwnership) {
        setActiveNote(item)
        setActiveMode('view')
      } else if (mode === 'view' && onViewNote) {
        onViewNote(item)
      } else {
        onEditNote?.(item)
      }
    },
    [hasContextNotesOwnership, mode, onViewNote, onEditNote],
  )

  const handleUnsavedSaveOpenSidebarNote =
    useCallback(async (): Promise<void> => {
      const saved = await saveCurrentNoteForLeave()
      if (!saved || !pendingSidebarNote) return
      openPendingSidebarNote(pendingSidebarNote)
      setPendingSidebarNote(null)
      setUnsavedModalType(null)
    }, [saveCurrentNoteForLeave, pendingSidebarNote, openPendingSidebarNote])

  const handleUnsavedDiscard = useCallback((): void => {
    if (unsavedModalType === 'openSidebarNote') {
      if (pendingSidebarNote) {
        openPendingSidebarNote(pendingSidebarNote)
      }
      setPendingSidebarNote(null)
      setUnsavedModalType(null)
      return
    }
    if (unsavedModalType === 'newNote') {
      handleNewNoteDiscardAndContinue()
      return
    }
    if (unsavedModalType === 'back') {
      handleBackDiscardAndLeave()
    }
  }, [
    unsavedModalType,
    pendingSidebarNote,
    openPendingSidebarNote,
    handleNewNoteDiscardAndContinue,
    handleBackDiscardAndLeave,
  ])

  const handleUnsavedSave = useCallback((): void => {
    if (unsavedModalType === 'openSidebarNote') {
      void handleUnsavedSaveOpenSidebarNote()
      return
    }
    if (unsavedModalType === 'newNote') {
      void handleNewNoteSaveAndContinue()
      return
    }
    if (unsavedModalType === 'back') {
      void handleBackSaveAndLeave()
    }
  }, [
    unsavedModalType,
    handleUnsavedSaveOpenSidebarNote,
    handleNewNoteSaveAndContinue,
    handleBackSaveAndLeave,
  ])

  const unsavedModalCopy = useMemo(() => {
    if (unsavedModalType === 'openSidebarNote') {
      return {
        message:
          'Please save your new changes in the current note before opening another one.',
      }
    }
    if (unsavedModalType === 'newNote') {
      return {
        message:
          'Please save your changes in the current note before starting a new one.',
      }
    }
    if (unsavedModalType === 'back') {
      return {
        message: 'Please save your changes before leaving.',
      }
    }
    return { message: '' }
  }, [unsavedModalType])

  const handleConfirmDelete = useCallback(() => {
    if (displayedNote?.note_id && (onDelete || hasContextId)) {
      effectiveOnDelete(displayedNote.note_id)
    }
  }, [displayedNote?.note_id, onDelete, hasContextId, effectiveOnDelete])

  const handleMagicEdit = useCallback(() => {
    setShowAiOverlay(true)
  }, [setShowAiOverlay])

  const handleToolbarEmojiSelect = useCallback((emoji: string) => {
    if (!emoji) return
    const editor = toolbarRef.current
    if (!editor) return

    if (savedEmojiSelectionRef.current) {
      try {
        const editorWithSelection = editor as RichTextEditorWithSelection
        const doc = editorWithSelection.contentModule?.getDocument?.()
        const range = savedEmojiSelectionRef.current?.restore()
        const nodeSelection =
          editorWithSelection.formatter?.editorManager?.nodeSelection
        if (nodeSelection && doc && range) {
          nodeSelection.setRange(doc, range)
        }
      } finally {
        savedEmojiSelectionRef.current = null
      }
    }

    editor.focusIn()
    editor.executeCommand('insertText', emoji)
    setShowHappyPopover(false)
    setShowVariablePopover(false)
  }, [])

  const handleToolbarMentionSelect = useCallback((name: string) => {
    if (!name) return
    toolbarRef.current?.focusIn()
    toolbarRef.current?.executeCommand('insertText', `@${name} `)
    setShowMentionPopover(false)
    setShowVariablePopover(false)
  }, [])

  const handleToolbarVariableSelect = useCallback((variableText: string) => {
    if (!variableText) return
    toolbarRef.current?.focusIn()
    toolbarRef.current?.executeCommand('insertText', `${variableText} `)
    setShowVariablePopover(false)
    setShowHappyPopover(false)
    setShowMentionPopover(false)
  }, [])

  const handleHappyClick = useCallback(() => {
    setShowMentionPopover(false)
    setShowVariablePopover(false)
    if (showHappyPopover) {
      setShowHappyPopover(false)
      return
    }
    const position = getToolbarButtonPosition('.custom-toolbar-button.happy')
    if (!position) {
      setShowHappyPopover(false)
      return
    }
    setHappyPopoverPosition(position)
    setShowHappyPopover(true)
  }, [showHappyPopover, getToolbarButtonPosition])

  const handleAtClick = useCallback(() => {
    setShowHappyPopover(false)
    setShowVariablePopover(false)
    if (showMentionPopover) {
      setShowMentionPopover(false)
      return
    }
    const position = getToolbarButtonPosition('.custom-toolbar-button.mention')
    if (!position) {
      setShowMentionPopover(false)
      return
    }
    setMentionPopoverPosition(position)
    setShowMentionPopover(true)
  }, [showMentionPopover, getToolbarButtonPosition])

  const handleVariableClick = useCallback(() => {
    setShowHappyPopover(false)
    setShowMentionPopover(false)
    if (showVariablePopover) {
      setShowVariablePopover(false)
      return
    }
    const position = getToolbarButtonPosition('.custom-toolbar-button.variable')
    if (!position) {
      setShowVariablePopover(false)
      return
    }
    setVariablePopoverPosition(position)
    setShowVariablePopover(true)
  }, [showVariablePopover, getToolbarButtonPosition])

  const handleSignatureClick = useCallback(() => {
    setShowSignaturePad(true)
  }, [])

  const handleSignatureSave = useCallback(() => {
    if (signatureRef.current) {
      const signatureData = signatureRef.current.getSignature()
      if (signatureData) {
        const signatureImage = `<img src="${signatureData}" alt="Signature" style="max-width: 200px; max-height: 100px; cursor: move;" draggable="true" />`
        const newContent =
          noteContent + (noteContent ? '<br>' : '') + signatureImage
        setNoteContent(newContent)
      }
    }
    setShowSignaturePad(false)
  }, [noteContent])

  const handleSignatureCancel = useCallback(() => {
    setShowSignaturePad(false)
  }, [])

  const handleToolbarActions = useCallback(
    (buttonName: string): void => {
      switch (buttonName) {
        case 'happy':
          handleHappyClick()
          break
        case 'mention':
          handleAtClick()
          break
        case 'variable':
          handleVariableClick()
          break
        case 'magic-edit':
          handleMagicEdit()
          break
        case 'template':
          handleTemplateClick()
          break
        case 'signature':
          handleSignatureClick()
          break
        default:
          break
      }
    },
    [
      handleHappyClick,
      handleAtClick,
      handleMagicEdit,
      handleTemplateClick,
      handleSignatureClick,
      handleVariableClick,
    ],
  )

  const handleQuickNoteSend = useCallback(() => {
    if (!canUseNotesTextAi) return
    const trimmedQuickNote = quickNote.trim()
    if (!trimmedQuickNote) return

    const isParaphrase = quickNoteAiMode === 'paraphrase'
    const userRequest = isParaphrase
      ? `Paraphrase the following text:\n${quickNote}`
      : quickNote
    const label = isParaphrase
      ? QUICK_NOTE_AI_MODE_CONFIG.paraphrase.label
      : trimmedQuickNote

    const transcriptSegments =
      transcriptSummarySegmentsRef.current.length > 0
        ? transcriptSummarySegmentsRef.current
        : persistableTranscriptRef.current
    const query = buildNotesAiUserSourceInput({
      existingNoteHtml: noteContent,
      transcriptSegments,
      userRequest,
    })

    setAiPreviewSource('quick-note')
    const featureKey = isParaphrase ? 'rewrite' : 'custom'
    lastNotesAiGenerationRef.current = {
      source: 'quick-note',
      query: userRequest,
      featureKey,
      label,
      segments: transcriptSegments.map((segment) => ({ ...segment })),
    }
    const { html: structureHtml, outline: structureOutline } =
      resolveAiStructureTemplate(aiNoteStructure)
    void generateResponse(query, {
      label,
      featureKey,
      structure: getAiOptionLabel(aiNoteStructureOptions, aiNoteStructure),
      structureHtml,
      structureOutline,
      tone: getAiOptionLabel(aiToneOptions, aiTone),
    })
    setQuickNote('')
    setIsTyping(false)
  }, [
    aiNoteStructure,
    aiNoteStructureOptions,
    aiTone,
    aiToneOptions,
    canUseNotesTextAi,
    generateResponse,
    getAiOptionLabel,
    noteContent,
    quickNote,
    quickNoteAiMode,
    resolveAiStructureTemplate,
  ])

  const handleRegenerate = useCallback(() => {
    if (!showAiOverlay || !canUseNotesTextAi) return
    // Rebuild from the current template/structure. Replaying the last request
    // drops a template chosen after the first generate.
    regenerateFromRecordingBarOptions()
  }, [canUseNotesTextAi, regenerateFromRecordingBarOptions, showAiOverlay])

  const handleEnhance = useCallback(
    (enhanceType: string) => {
      if (!showAiOverlay || !canUseNotesTextAi) return
      void enhanceResponse(enhanceType)
    },
    [showAiOverlay, enhanceResponse, canUseNotesTextAi],
  )

  const handleReplaceAiResponse = useCallback(() => {
    if (!canCommitAiResponse) return
    setNoteContent(replaceResponse(null))
    setIsTyping(false)
    aiCommittedToNoteRef.current = true
    pendingAutoReplaceInsertedAiRef.current = false
    setAiPreviewSource(null)
    resetAIState()
  }, [canCommitAiResponse, replaceResponse, resetAIState])

  const handleInsertAiResponse = useCallback(() => {
    if (!canCommitAiResponse) return
    setNoteContent((prevContent) => insertResponse(prevContent))
    setIsTyping(false)
    aiCommittedToNoteRef.current = true
    pendingAutoReplaceInsertedAiRef.current = false
    setAiPreviewSource(null)
    resetAIState()

    if (quickNoteInputRef.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (quickNoteInputRef.current) {
            const input = quickNoteInputRef.current
            input.value = ''
            setQuickNote('')

            input.focus()

            input.setSelectionRange(0, 0)

            input.scrollLeft = 0
          }
        }, 50)
      })
    }
  }, [canCommitAiResponse, insertResponse, resetAIState])

  const handleDismissAiResponse = useCallback(() => {
    pendingAutoReplaceInsertedAiRef.current = false
    setIsTyping(false)
    setAiPreviewSource(null)
    resetAIState()
  }, [resetAIState])

  const handleQuickNoteKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleQuickNoteSend()
      }
    },
    [handleQuickNoteSend],
  )

  useEffect(() => {
    const handleGlobalKeyPress = (e: KeyboardEvent): void => {
      // Ignore synthetic Escape from Syncfusion popup cleanup (dispatched on click).
      if (e.key === 'Escape' && shouldShowAiPreview && e.isTrusted) {
        e.preventDefault()
        e.stopPropagation()
        handleDismissAiResponse()
        return
      }
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        setShowAiOverlay(true)
        setIsTyping(false)
      }
    }
    document.addEventListener('keydown', handleGlobalKeyPress, true)
    return () =>
      document.removeEventListener('keydown', handleGlobalKeyPress, true)
  }, [handleDismissAiResponse, shouldShowAiPreview])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(e.target as Node)
      ) {
        setShowTagDropdown(false)
      }
      if (
        templateDropdownRef.current &&
        !templateDropdownRef.current.contains(e.target as Node)
      ) {
        const templateButton = document.querySelector(
          '.rich-text-editor-container .custom-toolbar-button.template',
        ) as HTMLElement | null
        if (templateButton && templateButton.contains(e.target as Node)) {
          return
        }
        setShowTemplateDropdown(false)
        setTemplateDropdownPosition(null)
        setTemplateSearchQuery('')
      }
      if (
        aiNoteStructureDropdownRef.current &&
        !aiNoteStructureDropdownRef.current.contains(e.target as Node)
      ) {
        setShowAiNoteStructureDropdown(false)
      }
      if (
        aiToneDropdownRef.current &&
        !aiToneDropdownRef.current.contains(e.target as Node)
      ) {
        setShowAiToneDropdown(false)
      }
      if (showHappyPopover) {
        const happyButton = document.querySelector(
          '.custom-toolbar-button.happy',
        )
        if (
          happyPopoverRef.current &&
          (happyPopoverRef.current.contains(e.target as Node) ||
            happyButton?.contains(e.target as Node))
        ) {
        } else {
          setShowHappyPopover(false)
        }
      }
      if (showMentionPopover) {
        const mentionButton = document.querySelector(
          '.custom-toolbar-button.mention',
        )
        if (
          mentionPopoverRef.current &&
          (mentionPopoverRef.current.contains(e.target as Node) ||
            mentionButton?.contains(e.target as Node))
        ) {
        } else {
          setShowMentionPopover(false)
        }
      }
    }

    if (
      showTagDropdown ||
      showTemplateDropdown ||
      showAiNoteStructureDropdown ||
      showAiToneDropdown ||
      showHappyPopover ||
      showMentionPopover
    ) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [
    showTagDropdown,
    showTemplateDropdown,
    setShowTemplateDropdown,
    setTemplateDropdownPosition,
    setTemplateSearchQuery,
    templateDropdownRef,
    showAiNoteStructureDropdown,
    showAiToneDropdown,
    showHappyPopover,
    showMentionPopover,
  ])

  useEffect(() => {
    const handleEmojiButtonMouseDown = (e: MouseEvent): void => {
      const target = e.target as Element
      if (!target.closest?.('.custom-toolbar-button.happy')) return
      const editor = toolbarRef.current as RichTextEditorWithSelection | null
      if (!editor || !editor.contentModule) return
      try {
        const doc = editor.contentModule.getDocument()
        const sel = doc.getSelection()
        const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null
        if (range) {
          const nodeSelection = editor.formatter?.editorManager?.nodeSelection
          if (nodeSelection) {
            savedEmojiSelectionRef.current = nodeSelection.save(range, doc)
          }
        }
      } catch {
        savedEmojiSelectionRef.current = null
      }
    }
    document.addEventListener('mousedown', handleEmojiButtonMouseDown, true)
    return () =>
      document.removeEventListener(
        'mousedown',
        handleEmojiButtonMouseDown,
        true,
      )
  }, [])

  useEffect(() => {
    if (!showHappyPopover && !showMentionPopover) return

    const updatePositions = (): void => {
      if (showHappyPopover) {
        setHappyPopoverPosition(
          getToolbarButtonPosition('.custom-toolbar-button.happy'),
        )
      }
      if (showMentionPopover) {
        setMentionPopoverPosition(
          getToolbarButtonPosition('.custom-toolbar-button.mention'),
        )
      }
    }

    updatePositions()

    window.addEventListener('resize', updatePositions)
    window.addEventListener('scroll', updatePositions, true)

    return () => {
      window.removeEventListener('resize', updatePositions)
      window.removeEventListener('scroll', updatePositions, true)
    }
  }, [showHappyPopover, showMentionPopover, getToolbarButtonPosition])

  const toolbarSettings: ToolbarSettingsModel = useMemo(() => {
    const baseItems = [
      'Undo',
      'Redo',
      '|',
      'Bold',
      'Italic',
      'Underline',
      'StrikeThrough',
      '|',
      'FontSize',
      'FontColor',
      'BackgroundColor',
      '|',
      'Formats',
      'Alignments',
      '|',
      'NumberFormatList',
      'BulletFormatList',
      '|',
      'Outdent',
      'Indent',
      '|',
      'CreateLink',
      'Image',
      '|',
    ]
    const aiButtons = generateAIToolbarButtons(handleToolbarActions)

    return {
      items: [...baseItems, ...aiButtons],
      enable: true,
    }
  }, [handleToolbarActions])

  return (
    <>
      <div
        dangerouslySetInnerHTML={{
          __html: generateAIToolbarStyles(AI_ICON_COLOR, showAiOverlay),
        }}
      />
      <div
        className="add-note-editor"
        style={isBodyRowLoading ? { display: 'none' } : undefined}
      >
        <NoteEditorHeader
          backLabel={backLabel}
          onBackClick={handleBackClick}
          selectFirstNotePending={Boolean(selectFirstNotePending)}
          isViewMode={isViewMode}
          isHeaderContextPending={isHeaderContextPending}
          headerContextName={headerContextName}
          displayedNote={displayedNote}
          versionSiblingPool={versionSiblingPool}
          noteTitle={noteTitle}
          onNoteTitleChange={setNoteTitle}
          micModeLabel={micModeLabel}
          isRecordingActive={isRecordingActive}
          isUpdatingTranscription={isUpdatingTranscription}
          micStatusLabel={micStatusLabel}
          isSmartMode={isSmartMode}
          isRecording={isRecording}
          isRecordingPaused={isRecordingPaused}
          isLiveTranscribeConnecting={isLiveTranscribeConnecting}
          onMicClick={handleMicClick}
          flashModeTestUrl={flashModeTestUrl}
          flashModeAudioRef={flashModeAudioRef}
          isFlashMode={isFlashMode}
          isFlashModeTesting={isFlashModeTesting}
          hasRecordedAudio={hasRecordedAudio}
          onResetRecordingRequest={handleResetRecordingRequest}
          onRecordingStop={handleRecordingStop}
          onRecordingPause={handleRecordingPause}
          onRecordingCircleClick={handleRecordingCircleClick}
          activeMic={activeMic}
          onActiveMicChange={setActiveMic}
          canEditDisplayedNoteVersion={canEditDisplayedNoteVersion}
          claimContextNotesReadOnly={claimContextNotesReadOnly}
          hasContextId={hasContextId}
          hasContextNotesOwnership={Boolean(hasContextNotesOwnership)}
          onSetActiveMode={setActiveMode}
          onEditNote={onEditNote}
          onCancel={handleCancelClick}
          onSave={handleSave}
        />
        <div
          className={
            [
              canShowNotesSidebar ? 'add-note-editor-body-row' : null,
              isBodyRowLoading ? 'add-note-editor-body-row-loading' : null,
            ]
              .filter(Boolean)
              .join(' ') || undefined
          }
          style={
            canShowNotesSidebar
              ? { flex: 1, minHeight: 0 }
              : {
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }
          }
        >
          <div className="notes-main-content-body">
            <NoteEditorRichTextPane
              richTextEditorContainerRef={richTextEditorContainerRef}
              isViewMode={isViewMode}
              isBodyRowLoading={Boolean(isBodyRowLoading)}
              noteContent={noteContent}
              resolvedContentForView={resolvedContentForView}
              viewDisplayContent={viewDisplayContent}
              rteKey={`note-rte-${displayedNote?.note_id ?? 'new'}-${rteEditKey}-${canShowNotesSidebar ? 'ctxsb' : 'noctxsb'}`}
              toolbarRef={toolbarRef}
              onRteChange={handleRteChange}
              toolbarSettings={toolbarSettings}
              showHappyPopover={showHappyPopover}
              happyPopoverPosition={happyPopoverPosition}
              happyPopoverRef={happyPopoverRef}
              onEmojiSelect={handleToolbarEmojiSelect}
              showMentionPopover={showMentionPopover}
              mentionPopoverPosition={mentionPopoverPosition}
              mentionPopoverRef={mentionPopoverRef}
              mentionOptions={mentionOptions}
              onMentionSelect={handleToolbarMentionSelect}
              showVariablePopover={showVariablePopover}
              variablePopoverPosition={variablePopoverPosition}
              variablePopoverRef={variablePopoverRef}
              noteVariableList={noteVariableList}
              onVariableSelect={handleToolbarVariableSelect}
              templateDropdown={{
                templateDropdownRef,
                showTemplateDropdown,
                templateDropdownPosition,
                templateSearchQuery,
                onTemplateSearch: handleTemplateSearch,
                selectedTemplate,
                customTemplates,
                aiNoteStructure,
                setAiNoteStructure,
                setSelectedTemplate,
                setTemplateButtonLabel,
                setShowTemplateDropdown,
                setTemplateDropdownPosition,
                expandedCategories,
                setExpandedCategories,
                filteredTemplates,
                setShowCustomTemplateModal,
                setEditingTemplateId,
                setNoteContent,
                getTemplateContent,
                getTemplateDisplayName,
              }}
            />
            {!isViewMode && (
              <NoteEditorQuickNote
                shouldShowAiRecordingBar={shouldShowAiRecordingBar}
                aiNoteStructureDropdownRef={aiNoteStructureDropdownRef}
                showAiNoteStructureDropdown={showAiNoteStructureDropdown}
                setShowAiNoteStructureDropdown={setShowAiNoteStructureDropdown}
                setShowAiToneDropdown={setShowAiToneDropdown}
                aiNoteStructureLabel={aiNoteStructureLabel}
                aiNoteStructureOptions={aiNoteStructureOptions}
                aiNoteStructure={aiNoteStructure}
                onAiNoteStructureSelect={handleAiNoteStructureSelect}
                aiToneDropdownRef={aiToneDropdownRef}
                showAiToneDropdown={showAiToneDropdown}
                aiToneLabel={aiToneLabel}
                aiToneOptions={aiToneOptions}
                aiTone={aiTone}
                onAiToneSelect={handleAiToneSelect}
                shouldShowAiPlaybackControls={shouldShowAiPlaybackControls}
                showRecordingTakeNav={showRecordingTakeNav}
                activeTakeIndex={activeTakeIndex}
                recordingTakes={recordingTakes}
                onPreviousRecordingTake={handlePreviousRecordingTake}
                audioUrl={audioUrl}
                onRecordingTakeEnded={handleRecordingTakeEnded}
                onNextRecordingTake={handleNextRecordingTake}
                showTranscription={showTranscription}
                onHideTranscription={onHideTranscription}
                onViewTranscription={onViewTranscription}
                shouldShowAiPreview={shouldShowAiPreview}
                aiResponse={aiResponse}
                showEnhanceDropdown={showEnhanceDropdown}
                isAiLoading={isAiLoading}
                aiError={aiError}
                onRegenerate={handleRegenerate}
                onEnhance={handleEnhance}
                setShowEnhanceDropdown={setShowEnhanceDropdown}
                quickNoteInputRef={quickNoteInputRef}
                quickNote={quickNote}
                setQuickNote={setQuickNote}
                quickNoteAiMode={quickNoteAiMode}
                setQuickNoteAiMode={setQuickNoteAiMode}
                showAiOverlay={showAiOverlay}
                setIsTyping={setIsTyping}
                isTyping={isTyping}
                onQuickNoteKeyPress={handleQuickNoteKeyPress}
                hasAiResponse={hasAiResponse}
                onQuickNoteSend={handleQuickNoteSend}
                hasQuickNoteText={hasQuickNoteText}
                canUseNotesTextAi={canUseNotesTextAi}
                notesTextAiDisabledTitle={notesTextAiDisabledTitle}
                onDismissAiResponse={handleDismissAiResponse}
                onReplaceAiResponse={handleReplaceAiResponse}
                onInsertAiResponse={handleInsertAiResponse}
                canCommitAiResponse={canCommitAiResponse}
              />
            )}
            {!isViewMode && (
              <NoteEditorNoteInfo
                tagDropdownRef={tagDropdownRef}
                tagLockedToContext={Boolean(tagLockedToContext)}
                onTagClick={handleTagClick}
                effectiveTag={effectiveTag}
                showTagDropdown={showTagDropdown}
                onTagSelect={handleTagSelect}
              />
            )}
          </div>
          {canShowNotesSidebar && showContextNotesSidebar && (
            <NoteEditorContextSidebar
              claimContextNotesReadOnly={claimContextNotesReadOnly}
              hasContextId={hasContextId}
              contextId={contextId}
              activeNote={activeNote}
              activeMode={activeMode}
              hasUnsavedChanges={hasUnsavedChanges}
              onProceedToNewNote={proceedToNewNote}
              setUnsavedModalType={setUnsavedModalType}
              contextType={contextType}
              isContactContext={isContactContext}
              sidebarSearchQuery={sidebarSearchQuery}
              setSidebarSearchQuery={setSidebarSearchQuery}
              onApplyFilters={sidebarApplyFilters}
              contextNotes={contextNotes}
              contextLookup={contextLookup}
              sidebarAppliedFilters={sidebarAppliedFilters}
              contextNotesFetched={contextNotesFetched}
              shouldDelaySidebarListRender={shouldDelaySidebarListRender}
              sortedSidebarFilteredNotes={sortedSidebarFilteredNotes}
              sidebarFilteredNotes={sidebarFilteredNotes}
              displayedNote={displayedNote}
              hasContextNotesOwnership={Boolean(hasContextNotesOwnership)}
              onEditNote={onEditNote}
              mode={mode}
              onViewNote={onViewNote}
              onBeforeOpenNote={() => {
                closeRteInlineDropdownPopups(richTextEditorContainerRef.current)
              }}
              setPendingSidebarNote={setPendingSidebarNote}
              setActiveNote={setActiveNote}
              setActiveMode={setActiveMode}
            />
          )}
        </div>
      </div>
      <NoteEditorModals
        showResetRecordingModal={showResetRecordingModal}
        onCancelResetRecording={handleCancelResetRecording}
        onConfirmResetRecording={handleConfirmResetRecording}
        showCustomTemplateModal={showCustomTemplateModal}
        onCloseCustomTemplate={() => {
          setShowCustomTemplateModal(false)
          setEditingTemplateId(null)
        }}
        onCustomTemplateSubmit={handleCustomTemplateSubmit}
        onCustomTemplateDelete={handleCustomTemplateDelete}
        editingTemplateId={editingTemplateId}
        customTemplates={customTemplates}
        showSuccessModal={showSuccessModal}
        noteTitle={noteTitle}
        effectiveTag={effectiveTag}
        isSavingNote={isSavingNote}
        onDiscardSave={() => setShowSuccessModal(false)}
        onConfirmSave={() => {
          void handleConfirmSave()
        }}
        displayedNote={displayedNote}
        onDelete={onDelete}
        hasContextId={hasContextId}
        showDeleteModal={showDeleteModal}
        onHideDelete={() => setShowDeleteModal(false)}
        onConfirmDelete={handleConfirmDelete}
        showSignaturePad={showSignaturePad}
        signatureRef={signatureRef}
        onSignatureCancel={handleSignatureCancel}
        onSignatureSave={handleSignatureSave}
        showEmptyNoteWarning={showEmptyNoteWarning}
        onEmptyNoteGotIt={handleEmptyNoteGotIt}
        onEmptyNoteDiscard={handleEmptyNoteDiscard}
        showUnsavedModal={unsavedModalType !== null}
        unsavedLeaveMessage={unsavedModalCopy.message}
        onUnsavedDismiss={handleUnsavedModalCancel}
        onUnsavedDiscard={handleUnsavedDiscard}
        onUnsavedSave={handleUnsavedSave}
      />
    </>
  )
}

export default NoteEditor
